import _, {isEqual, isUndefined} from 'lodash';
import {batch} from 'react-redux';

import {
  addQueryToPath,
  buildConversationContentUrl,
  buildConversationUrl,
} from '../../../../libs/shared/helpers/src/browser/urlHelpers';
import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {ErrorWithData} from '../../../../libs/shared/helpers/src/types/customErrors';
import {makeEnumRuntimeType} from '../../../../libs/shared/helpers/src/typescript/enumHelpers';
import {isDefined} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {randomString} from '../../../../libs/shared/helpers/src/utilities/stringHelpers';
import {ScrollDirectionsEnum} from '../../../../srcCommon/scroll/scrollerTypes';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {logger} from '../../../../srcCommon/services/logger/logger';
import {fetchAndCacheApiAsync} from '../../../../srcCommon/services/offline/offlineFetch';
import {CacheResponseInfo} from '../../../../srcCommon/services/offline/offlineResourcesCommon';
import {
  addPerformanceInteractionMetadata,
  cancelPerformanceInteraction,
  markPerformanceInteractionStep,
  markPerformanceInteractionStepOnAnimationFrame,
} from '../../../../srcCommon/services/performanceMetrics/interactionPerformance';
import {PerformanceInteractionStepsEnum} from '../../../../srcCommon/services/performanceMetrics/interactionPerformanceTypes';
import {CancelError, CancelToken} from '../../../helpers/asyncCancelHelpers';
import {
  findLastActivityDate,
  isActivityInConversationRuleNotification,
  isActivityInConversationUnreadMention,
} from '../../../helpers/models/conversationHelpers';
import {ActionWithPayload} from '../../../helpers/reduxHelpers';
import {ConversationTimelineBoundary, ConversationTimelineMetadata} from '../../../helpers/viewerHelpers';
import {ActivityVisualTypesEnum} from '../../../models/api/activityModel';
import {CommentStatusesEnum} from '../../../models/api/commentModel';
import {ConversationModel} from '../../../models/api/conversationModel';
import {
  ApiActivityPageDataGroup,
  ApiConversationContentDataGroup,
  ApiConversationDataGroup,
  DataGroupModel,
  mergeDataGroups,
} from '../../../models/api/dataGroupModel';
import {MarkAsReadDelaysEnum} from '../../../models/api/preferencesModel';
import {TeammatesFoldersEnum} from '../../../models/url/inboxesUrlModel';
import {findCommentComposer} from '../../../services/composers';
import {fetchApiAsync} from '../../../services/http/http';
import {ServerResponseInfo} from '../../../services/http/httpBase';
import {HttpError, HttpNetworkError, HttpResponseError} from '../../../services/http/httpError';
import {findPanelSequence} from '../../../services/panels';
import {buildPanelCancelToken, findPanelCancelToken} from '../../../services/panelsCancellers';
import {ResponseInfo} from '../../../services/performanceMetrics/legacyPerformanceMiddleware';
import {viewerFetchPerformanceId} from '../../../services/performanceMetrics/performanceIdHelpers';
import {InterfaceEventsEnum, PopoutLocationsEnum, trackInterfaceEvent} from '../../../services/tracking';
import {AppAction} from '../../appActions';
import {batchStore} from '../../appBatching';
import {AppState} from '../../appState';
import {Dispatch, StateGetter} from '../../appStore';
import {ThunkAction, ThunkAsyncAction} from '../../appThunk';
import {AsyncMoreStatusesEnum} from '../../appTypes';
import {getCanEditComments, getCompany} from '../../data/company/companySelectors';
import {
  conversationAccessDeniedAction,
  conversationContentUpdatedAction,
  conversationDeletedAction,
} from '../../data/conversations/conversationsActions';
import {
  makeGetConversationById,
  makeGetConversationContentUpdatedAt,
  makeGetDraftComments,
} from '../../data/conversations/conversationsSelectors';
import {toggleMarkConversationAsSeenAction} from '../../data/conversations/seenConversationsActions';
import {updateDataAction} from '../../data/dataGroupsActions';
import {
  getComments,
  getConversations,
  getIsImpersonating,
  getOriginalConversations,
} from '../../data/dataSelectors';
import {getMarkAsReadDelay} from '../../data/preferences/preferencesSelectors';
import {getUserAlias, getUserId} from '../../data/teammates/teammatesSelectors';
import {getHasOfflineCache} from '../../featureFlags/featureFlagsSelectors';
import {CellsContext, CellsContextTypesEnum} from '../cells/context/cellsContext';
import {makeCellsContext} from '../cells/context/cellsContextHelpers';
import {
  computeIsEmptyVisualSelection,
  findOnlyVisuallySelectedId,
} from '../cells/helpers/cellsPanelSelectionHelpers';
import {makeGetConversationSelection} from '../cells/selectors/cellsPanelSelectionSelectors';
import {
  InboxesConversationsParameters,
  InboxesParameters,
  InboxesParametersActivity,
  InboxesParametersTypesEnum,
} from '../inboxes/parameters/inboxesParameters';
import {
  areParametersCellsEqual,
  findParametersActivityDate,
  findParametersActivityId,
  normalInboxesSelection,
} from '../inboxes/parameters/inboxesParametersHelpers';
import {getBuildDefaultInboxesParameters} from '../inboxes/parameters/inboxesParametersSelectors';
import {PanelCancelTokenId} from '../panelCancelTokenId';
import {PanelActionPayload, setPanelAction} from '../panelsActions';
import {getFirstAppPanelId, getPanels} from '../panelsSelectors';
import {
  AppPanelId,
  makeCellsPanelId,
  makeViewerPanelId,
  PanelTypesEnum,
  tryFindInboxesPanelIdInPanelId,
  ViewerPanelId,
} from '../panelStateBase';
import {ActivityViewerActions} from './activity/activityViewerActions';
import {
  computeConversationContentFetchOptions,
  fetchInitialConversationFromDataGroup,
  fetchMoreConversationFromDataGroup,
  isConversationAndActivityOnPage,
} from './viewerPanelHelpers';
import {
  makeGetContentPage,
  makeGetPageActivities,
  makeGetTopbarPage,
  makeGetViewerPanel,
  makeGetViewerPanelPage,
} from './viewerPanelSelectors';
import {
  ViewerPanelConversationPageState,
  ViewerPanelConversationState,
  ViewerPanelEmptyPageState,
  ViewerPanelPageState,
  ViewerPanelPageTypesEnum,
  ViewerPanelState,
} from './viewerPanelState';

/*
 * Type enum.
 */

export enum ViewerPanelActionsEnum {
  NAVIGATED = '@@panels/viewer/NAVIGATED',
  // TODO: Rename these action types to past participle form.
  EDIT_COMMENT = '@@panels/viewer/EDIT_COMMENT',
  EDIT_DRAFT_MESSAGE = '@@panels/viewer/EDIT_DRAFT_MESSAGE',
  FORCE_INTERNAL_COMPOSER_POPOUT = '@@panels/viewer/FORCE_INTERNAL_COMPOSER_POPOUT',
}

export enum ViewerPanelPageActionsEnum {
  REFETCH_STARTED = '@@panels/viewer/REFETCH_STARTED',
  FETCH_CONVERSATION_COMPLETED = '@@panels/viewer/FETCH_CONVERSATION_COMPLETED',
  FETCH_CONVERSATION_FAILED = '@@panels/viewer/FETCH_CONVERSATION_FAILED',
  FETCH_CONVERSATION_GRACE_TIMEOUT = '@@panels/viewer/FETCH_CONVERSATION_GRACE_TIMEOUT',
  FETCH_MORE_CONVERSATION = '@@panels/viewer/FETCH_MORE_CONVERSATION',
  FETCH_MORE_CONVERSATION_COMPLETED = '@@panels/viewer/FETCH_MORE_CONVERSATION_COMPLETED',
  FETCH_MORE_CONVERSATION_FAILED = '@@panels/viewer/FETCH_MORE_CONVERSATION_FAILED',
  JUMPED = '@@panels/viewer/JUMPED',
  ALL_MESSAGES_COLLAPSED = '@@panels/viewer/ALL_MESSAGES_COLLAPSED',
}
const RViewerPanelPageAction = makeEnumRuntimeType(ViewerPanelPageActionsEnum);

/*
 * Types.
 */

type ViewerPanelActionPayload = PanelActionPayload<ViewerPanelId>;

interface ViewerNavigatedActionPayload extends ViewerPanelActionPayload {
  readonly page: ViewerPanelPageState;
  readonly cancelTokenId: PanelCancelTokenId | undefined;
}

interface ViewerNavigatedAction extends ActionWithPayload<ViewerNavigatedActionPayload> {
  type: ViewerPanelActionsEnum.NAVIGATED;
}

interface EditCommentContext extends ViewerPanelActionPayload {
  commentUid?: string;
}
interface EditCommentAction extends ActionWithPayload<EditCommentContext> {
  type: ViewerPanelActionsEnum.EDIT_COMMENT;
}

interface EditDraftMessageContext extends ViewerPanelActionPayload {
  messageUid: string;
}
interface EditDraftMessageAction extends ActionWithPayload<EditDraftMessageContext> {
  type: ViewerPanelActionsEnum.EDIT_DRAFT_MESSAGE;
}

interface ViewerPanelPageActionPayload extends ViewerPanelActionPayload {
  readonly pageUid: string;
}

interface ViewerPageRefetchStartedActionPayload extends ViewerPanelPageActionPayload {
  readonly cancelTokenId: PanelCancelTokenId;
}
interface ViewerPageRefetchStartedAction extends ActionWithPayload<ViewerPageRefetchStartedActionPayload> {
  type: ViewerPanelPageActionsEnum.REFETCH_STARTED;
}

interface FetchConversationCompletedActionPayload
  extends ViewerPanelPageActionPayload,
    ConversationTimelineMetadata {
  readonly conversationState: ViewerPanelConversationState;
  /** The "around activity" used for the fetch, since it may be different from the requested parameters. */
  readonly aroundActivity: InboxesParametersActivity | undefined;
  readonly responseInfo?: ResponseInfo;
}

interface FetchConversationCompletedAction
  extends ActionWithPayload<FetchConversationCompletedActionPayload> {
  type: ViewerPanelPageActionsEnum.FETCH_CONVERSATION_COMPLETED;
}
interface FetchConversationFailedAction extends ActionWithPayload<ViewerPanelPageActionPayload> {
  type: ViewerPanelPageActionsEnum.FETCH_CONVERSATION_FAILED;
}
interface FetchConversationGraceTimeout extends ActionWithPayload<ViewerPanelPageActionPayload> {
  type: ViewerPanelPageActionsEnum.FETCH_CONVERSATION_GRACE_TIMEOUT;
}

interface FetchMoreConversationActionPayload extends ViewerPanelPageActionPayload {
  direction: ScrollDirectionsEnum;
}

interface FetchMoreConversationCompletedActionPayload
  extends FetchMoreConversationActionPayload,
    ConversationTimelineMetadata {}

interface FetchMoreConversationStartedAction extends ActionWithPayload<FetchMoreConversationActionPayload> {
  type: ViewerPanelPageActionsEnum.FETCH_MORE_CONVERSATION;
}
interface FetchMoreConversationCompletedAction
  extends ActionWithPayload<FetchMoreConversationCompletedActionPayload> {
  type: ViewerPanelPageActionsEnum.FETCH_MORE_CONVERSATION_COMPLETED;
}
interface FetchMoreConversationFailedAction extends ActionWithPayload<FetchMoreConversationActionPayload> {
  type: ViewerPanelPageActionsEnum.FETCH_MORE_CONVERSATION_FAILED;
}

interface JumpedActionPayload extends ViewerPanelPageActionPayload {
  readonly jumpUid: string;
  readonly targetActivity: InboxesParametersActivity;
  readonly sourceActivityId?: number;
}
interface JumpedAction extends ActionWithPayload<JumpedActionPayload> {
  type: ViewerPanelPageActionsEnum.JUMPED;
}

interface AllMessagesCollapsedAction extends ActionWithPayload<ViewerPanelPageActionPayload> {
  type: ViewerPanelPageActionsEnum.ALL_MESSAGES_COLLAPSED;
}

interface ForceInternalComposerPopoutAction extends ActionWithPayload<PanelActionPayload<ViewerPanelId>> {
  type: ViewerPanelActionsEnum.FORCE_INTERNAL_COMPOSER_POPOUT;
}

/*
 * Action creators.
 */

interface NavigateViewerOptions {
  popupAppPanelId?: AppPanelId;
  isAlreadyFullyLoaded?: boolean;
  jumpSourceActivityId?: number;
  shouldExpandMessages?: boolean;
  cachePolicy?: FetchConversationCachePoliciesEnum;
}

export function refetchViewerAsyncAction(panelId: ViewerPanelId): ThunkAsyncAction {
  const getViewerPanel = makeGetViewerPanel();
  const getPage = makeGetViewerPanelPage();

  return async (dispatch, getState) => {
    const panel = getViewerPanel(getState(), {panelId});
    const page = getPage(getState(), {panelId});
    if (page.type !== ViewerPanelPageTypesEnum.SINGLE_CONVERSATION) {
      return;
    }

    // Start a refetch from the network.
    const [cancelTokenId, cancelToken] = buildPanelCancelToken(panelId, panel.cancelTokenId);
    const cachePolicy = FetchConversationCachePoliciesEnum.NETWORK_ONLY;
    const fetchPromise = dispatch(fetchConversationAsyncAction(panelId, page, cachePolicy, cancelToken));

    // Update the panel.
    const pageUid = page.uid;
    dispatch({
      type: ViewerPanelPageActionsEnum.REFETCH_STARTED,
      payload: {
        panelId,
        pageUid,
        cancelTokenId,
      },
    });

    try {
      await fetchPromise;
    } catch (error) {
      if (error instanceof CancelError) {
        return;
      }

      throw error;
    }
  };
}

export function navigateViewerAsyncAction(
  panelId: ViewerPanelId,
  parameters: InboxesParameters,
  options: NavigateViewerOptions = {},
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    // Find the viewer panel with this ID.
    const panel = getPanels(getState())[panelId];
    if (panel && panel.type !== PanelTypesEnum.VIEWER) {
      throw new Error('Wrong panel type.');
    }

    // Check if there's any need to create a new page.
    const existingPage = panel?.pages.next || panel?.pages.current;
    const page = buildViewerPage(getState(), panelId, parameters, existingPage, options);
    const {popupAppPanelId} = options;

    if (existingPage && !shouldRefresh(page, existingPage)) {
      return;
    }

    // Fetch the conversation for the new parameters.
    const [cancelTokenId, cancelToken] = buildPanelCancelToken(panelId, panel?.cancelTokenId);
    const cachePolicy = options.cachePolicy ?? FetchConversationCachePoliciesEnum.CACHE_THEN_NETWORK;
    const pagePromise =
      page.type === ViewerPanelPageTypesEnum.SINGLE_CONVERSATION
        ? dispatch(navigateViewerConversationPageAsyncAction(panelId, page, cachePolicy, cancelToken))
        : Promise.resolve();

    // Update or create the panel.
    if (panel) {
      dispatch<ViewerNavigatedAction>({
        type: ViewerPanelActionsEnum.NAVIGATED,
        payload: {
          panelId,
          page,
          cancelTokenId,
        },
      });

      // After a grace period, make sure the new page is displayed.
      setTimeout(() => {
        dispatch<FetchConversationGraceTimeout>({
          type: ViewerPanelPageActionsEnum.FETCH_CONVERSATION_GRACE_TIMEOUT,
          payload: {
            panelId,
            pageUid: page.uid,
          },
        });
      }, 500);
    } else {
      dispatch(
        setPanelAction({
          id: panelId,
          type: PanelTypesEnum.VIEWER,
          popupAppPanelId,
          shouldForcePopout: Boolean(popupAppPanelId),
          pages: {current: page, next: undefined},
          composeCommentUid: undefined,
          cancelTokenId,
          sequence: findPanelSequence(),
        }),
      );
    }

    try {
      await pagePromise;
    } catch (error) {
      if (error instanceof CancelError) {
        return;
      }

      throw error;
    }
  };
}

function navigateViewerConversationPageAsyncAction(
  panelId: ViewerPanelId,
  page: ViewerPanelConversationPageState,
  cachePolicy: FetchConversationCachePoliciesEnum,
  cancelToken: CancelToken,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const fetchPromise = dispatch(fetchConversationAsyncAction(panelId, page, cachePolicy, cancelToken));

    const state = getState();
    const isImpersonating = getIsImpersonating(state);
    const markAsReadDelay = getMarkAsReadDelay(state);
    if (!isImpersonating && markAsReadDelay === MarkAsReadDelaysEnum.NO_DELAY_ON_SELECTION) {
      dispatch(
        toggleMarkConversationAsSeenAction([page.conversationId], true, {
          cellsContext: page.cellsContext,
        }),
      );
    }

    await fetchPromise;
  };
}

function buildViewerPage(
  state: AppState,
  panelId: ViewerPanelId,
  parameters: InboxesParameters,
  existingPage: ViewerPanelPageState | undefined,
  options: NavigateViewerOptions,
): ViewerPanelPageState {
  const cellsContext = makeCellsContext(state, parameters);
  const conversationSelection = makeGetConversationSelection<void>(
    () => parameters,
    () => {
      const inboxesPanelId = tryFindInboxesPanelIdInPanelId(panelId);
      return inboxesPanelId ? makeCellsPanelId(inboxesPanelId) : undefined;
    },
  )(state);

  // No conversation.
  if (computeIsEmptyVisualSelection(conversationSelection)) {
    return {
      uid: randomString(),
      type: ViewerPanelPageTypesEnum.NO_CONVERSATION,
      parameters,
      cellsContext,
    };
  }

  const conversationId = findOnlyVisuallySelectedId(conversationSelection);

  // Multiple conversations.
  if (!conversationId) {
    return {
      uid: randomString(),
      type: ViewerPanelPageTypesEnum.MULTI_CONVERSATIONS,
      parameters,
      cellsContext,
    };
  }

  // Capture the state of the conversation when navigating and before marking it as seen.
  const conversation = getConversations(state)[conversationId];
  const conversationState = conversation && {
    lastActivityDate: findLastActivityDate(conversation, cellsContext.teammateId),
    seenAt: conversation.seenAt,
    autoSeenAt: conversation.autoSeenAt,
  };

  // If the next page is for the same conversation, retain that state.
  const originalConversationState =
    existingPage?.type === ViewerPanelPageTypesEnum.SINGLE_CONVERSATION &&
    existingPage.conversationId === conversationId
      ? existingPage.originalConversationState
      : conversationState;

  const statuses = options.isAlreadyFullyLoaded
    ? {
        status: AsyncStatusesEnum.LOADED,
        moreStatusTop: AsyncMoreStatusesEnum.ALL_LOADED,
        moreStatusBottom: AsyncMoreStatusesEnum.ALL_LOADED,
      }
    : {
        status: AsyncStatusesEnum.LOADING,
        moreStatusTop: AsyncMoreStatusesEnum.NONE,
        moreStatusBottom: AsyncMoreStatusesEnum.NONE,
      };

  return {
    uid: randomString(),
    type: ViewerPanelPageTypesEnum.SINGLE_CONVERSATION,
    parameters,
    cellsContext,
    conversationId,
    activities: {},
    draftMessageSequences: {},
    topBoundary: undefined,
    bottomBoundary: undefined,
    shouldExpandMessages: Boolean(options.shouldExpandMessages),
    jumpSourceActivityId: options.jumpSourceActivityId,
    originalConversationState,
    ...statuses,
  };
}

function shouldRefresh(page: ViewerPanelPageState, previousPage: ViewerPanelPageState | undefined) {
  if (!previousPage) {
    return true;
  }

  switch (page.type) {
    case ViewerPanelPageTypesEnum.SINGLE_CONVERSATION:
      return (
        page.type !== previousPage.type ||
        previousPage.status === AsyncStatusesEnum.FAILED ||
        !areParametersCellsEqual(page.parameters, previousPage.parameters) ||
        page.conversationId !== previousPage.conversationId ||
        !isEqual(page.parameters.activity, previousPage.parameters.activity) ||
        (page.shouldExpandMessages && !previousPage.shouldExpandMessages)
      );

    default:
      return (
        page.type !== previousPage.type ||
        !areParametersCellsEqual(page.parameters, previousPage.parameters) ||
        !isEqual(page.parameters.selection, previousPage.parameters.selection)
      );
  }
}

export enum FetchConversationCachePoliciesEnum {
  /* Don't read or write from cache. */
  NO_CACHE = 'NO_CACHE',
  /* Don't read from cache, but write results from the network to the cache. */
  NETWORK_ONLY = 'NETWORK_ONLY',
  /* Read from the cache, and also fetch from the network to validate. */
  CACHE_THEN_NETWORK = 'CACHE_THEN_NETWORK',
}

export function fetchConversationAsyncAction(
  panelId: ViewerPanelId,
  page: ViewerPanelConversationPageState,
  cachePolicy: FetchConversationCachePoliciesEnum,
  cancelToken: CancelToken,
): ThunkAsyncAction {
  const performanceId = viewerFetchPerformanceId.get();

  const {parameters} = page;
  const aroundActivityId = findParametersActivityId(parameters);
  const aroundActivityDate = findParametersActivityDate(parameters);

  return async (dispatch, getState) => {
    // If we can, use the cache.
    if (
      cachePolicy === FetchConversationCachePoliciesEnum.CACHE_THEN_NETWORK &&
      getHasOfflineCache(getState()) &&
      isUndefined(aroundActivityId) &&
      !(isDefined(aroundActivityDate) && isFinite(aroundActivityDate))
    ) {
      return dispatch(
        fetchConversationFromCacheOrServerAsyncAction(panelId, page, cancelToken, performanceId),
      );
    }

    // Otherwise, fetch from the server.
    return dispatch(fetchConversationFromServerAsyncAction(panelId, page, cancelToken, performanceId));
  };
}

function maybeHandleConversationResponseErrorAction(conversationId: number, error: unknown): ThunkAction {
  return (dispatch) => {
    const responseErrorAction = maybeConversationResponseErrorAction(conversationId, error);
    if (!responseErrorAction) {
      return;
    }

    dispatch(responseErrorAction);
  };
}

function maybeConversationResponseErrorAction(conversationId: number, error: unknown) {
  if (!(error instanceof HttpResponseError)) {
    return undefined;
  }

  if (error.status === 403) {
    return conversationAccessDeniedAction(conversationId);
  }

  if (error.status === 404) {
    return conversationDeletedAction(conversationId);
  }

  return undefined;
}

function fetchConversationFromServerAsyncAction(
  panelId: ViewerPanelId,
  page: ViewerPanelConversationPageState,
  cancelToken: CancelToken,
  performanceId: string | undefined,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const {parameters, cellsContext, conversationId, shouldExpandMessages} = page;

    if (performanceId) {
      addPerformanceInteractionMetadata(performanceId, 'did_attempt_cache_fetch', true);
    }

    let responseInfo: ResponseInfo | undefined;

    const onResponseReceived = (info: ServerResponseInfo) => {
      responseInfo = {...info, cacheMiss: false};
    };

    const aroundActivityId = findParametersActivityId(parameters);
    const aroundActivityDate = findParametersActivityDate(parameters);
    const finiteAroundActivityDate =
      isDefined(aroundActivityDate) && isFinite(aroundActivityDate) ? aroundActivityDate : undefined;
    const shouldExpandMessagesByDate = shouldExpandMessages || isDefined(finiteAroundActivityDate);

    // Start the request right away. No time like the present.
    const company = getCompany(getState());
    const path = buildConversationUrl(conversationId, {
      aroundActivityId,
      aroundActivityDate: finiteAroundActivityDate,
      expandMessages: shouldExpandMessagesByDate,
    });
    const requestPromise = fetchApiAsync(ApiConversationDataGroup, `${company.url}${path}`, {
      cancelToken,
      onResponseReceived,
    });

    try {
      // Wait for the request to complete.
      const dataGroup = await requestPromise;
      if (performanceId) {
        markPerformanceInteractionStep(performanceId, PerformanceInteractionStepsEnum.NETWORK_FETCH);
      }

      const conversation = dataGroup.conversations?.find((candidate) => candidate.id === conversationId);
      if (!conversation) {
        throw new ErrorWithData('Missing conversation in ApiConversationDataGroup', {conversationId});
      }

      const conversationState: ViewerPanelConversationState = {
        lastActivityDate: findLastActivityDate(conversation, cellsContext.teammateId),
        seenAt: conversation.seenAt,
        autoSeenAt: conversation.autoSeenAt,
      };

      const fetchResult = fetchInitialConversationFromDataGroup(dataGroup);

      // Update the state with the result.
      // Note: we don't track metrics for this request as it can mess up our caching stats: We want to fetch from the server purposely here.
      batchStore(() => {
        dispatch(updateDataAction(dataGroup, {shouldNotCascade: true}));
        dispatch<FetchConversationCompletedAction>({
          type: ViewerPanelPageActionsEnum.FETCH_CONVERSATION_COMPLETED,
          payload: {
            panelId,
            pageUid: page.uid,
            ...fetchResult,
            conversationState,
            aroundActivity: parameters.activity,
            responseInfo,
          },
        });
      });
    } catch (error) {
      cancelPerformanceInteraction(performanceId);

      // Update the state with the error, unless it is a cancellation or we're refreshing.
      // TODO: Tell the user the timeline might now be outdated because refreshing failed.
      if (!(error instanceof CancelError)) {
        dispatch<FetchConversationFailedAction>({
          type: ViewerPanelPageActionsEnum.FETCH_CONVERSATION_FAILED,
          payload: {panelId, pageUid: page.uid},
        });
      }

      dispatch(maybeHandleConversationResponseErrorAction(conversationId, error));

      throw error;
    }

    markPerformanceInteractionStepOnAnimationFrame(
      performanceId,
      PerformanceInteractionStepsEnum.RENDER,
      true,
    );
  };
}

function fetchConversationFromCacheOrServerAsyncAction(
  panelId: ViewerPanelId,
  page: ViewerPanelConversationPageState,
  cancelToken: CancelToken,
  performanceId: string | undefined,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const company = getCompany(state);
    const userAlias = getUserAlias(state);
    const {cellsContext, conversationId} = page;

    if (performanceId) {
      addPerformanceInteractionMetadata(performanceId, 'did_attempt_cache_fetch', true);
    }

    let responseInfo: ResponseInfo | undefined;
    let didFetchFromCache = false;

    const onServerResponseReceived = (info: ServerResponseInfo) => {
      if (didFetchFromCache) {
        // We have already fetched from the cache and the server fetch is just for revalidation.
        return;
      }

      if (performanceId) {
        markPerformanceInteractionStep(performanceId, PerformanceInteractionStepsEnum.NETWORK_FETCH);
      }
      responseInfo = {...info, cacheMiss: true};
    };

    const onCachedResponseReceived = (info: CacheResponseInfo) => {
      if (performanceId) {
        markPerformanceInteractionStep(performanceId, PerformanceInteractionStepsEnum.CACHE_FETCH);
      }
      didFetchFromCache = true;
      responseInfo = info;
    };

    const onRefetchResponseReceived = (info: ServerResponseInfo) => {
      if (performanceId) {
        addPerformanceInteractionMetadata(performanceId, 'did_refetch_content', true);
      }
      responseInfo = {...info, cacheMiss: false};
    };

    // Start the request right away.
    const resultPromise =
      // We fetch the conversation and its content separately so that we can cache the content independently.
      // The alternative would be to extract the content pieces from the overall datagroup, but it's not possible to do that
      // without a loss of information.
      Promise.all([
        fetchApiConversationAsync(conversationId, getState, cancelToken, performanceId),
        fetchApiConversationContentAsync({
          conversationId,
          getState,
          dispatch,
          cancelToken,
          onServerResponseReceived,
          onCachedResponseReceived,
        }),
      ]);

    try {
      // Wait for the request to complete.
      const [conversationDataGroup, contentDataGroup] = await resultPromise;

      const conversation = conversationDataGroup.conversations?.find(
        (candidate) => candidate.id === conversationId,
      );
      if (!conversation) {
        throw new ErrorWithData('Missing conversation in fetchApiConversationAsync data group', {
          conversationId,
        });
      }

      const conversationState: ViewerPanelConversationState = {
        lastActivityDate: findLastActivityDate(conversation, cellsContext.teammateId),
        seenAt: conversation.seenAt,
        autoSeenAt: conversation.autoSeenAt,
      };

      // Maybe refetch content if we are online and our view should include activities earlier than the last 20
      const {aroundActivity, contentDataGroup: updatedContentDataGroup} =
        await maybeRefetchConversationAroundDateAsync(
          cellsContext,
          conversationId,
          page.parameters.activity,
          page.originalConversationState ?? conversationState,
          contentDataGroup,
          company.url,
          userAlias,
          onRefetchResponseReceived,
          cancelToken,
        );

      const dataGroup = mergeConversationAndContentDataGroups(
        conversationId,
        getState,
        conversationDataGroup,
        updatedContentDataGroup,
      );

      // Update the state with the result.
      batchStore(() => {
        dispatch(
          updateDataAction(dataGroup, {
            shouldNotCascade: true,
            isComingFromViewerFetch: true,
          }),
        );
        dispatch(conversationContentUpdatedAction(conversationId, updatedContentDataGroup));

        const fetchResult = fetchInitialConversationFromDataGroup(dataGroup);

        dispatch<FetchConversationCompletedAction>({
          type: ViewerPanelPageActionsEnum.FETCH_CONVERSATION_COMPLETED,
          payload: {
            panelId,
            pageUid: page.uid,
            ...fetchResult,
            conversationState,
            aroundActivity,
            responseInfo,
          },
        });
      });
    } catch (error) {
      cancelPerformanceInteraction(performanceId);

      // If this a cancellation, no need to update the state.
      if (error instanceof CancelError) {
        throw error;
      }

      // Update the state with the error.
      const requestFailedAction: FetchConversationFailedAction = {
        type: ViewerPanelPageActionsEnum.FETCH_CONVERSATION_FAILED,
        payload: {panelId, pageUid: page.uid},
      };
      dispatch(requestFailedAction);

      dispatch(maybeHandleConversationResponseErrorAction(conversationId, error));

      throw error;
    }

    markPerformanceInteractionStepOnAnimationFrame(
      performanceId,
      PerformanceInteractionStepsEnum.RENDER,
      true,
    );
  };
}

interface RefetchConversationResult {
  aroundActivity: InboxesParametersActivity | undefined;
  contentDataGroup: DataGroupModel;
}

async function maybeRefetchConversationAroundDateAsync(
  cellsContext: CellsContext,
  conversationId: number,
  aroundActivity: InboxesParametersActivity | undefined,
  conversationState: ViewerPanelConversationState,
  contentDataGroup: DataGroupModel,
  companyUrl: string,
  userAlias: string,
  onRefetchResponseReceived: (info: ServerResponseInfo) => void,
  cancelToken: CancelToken,
): Promise<RefetchConversationResult> {
  // If a specific spot in the timeline was requested, leave it alone.
  if (aroundActivity) {
    return {contentDataGroup, aroundActivity};
  }

  // Nothing extra to process if we're not in a private inbox.
  if (cellsContext.type !== CellsContextTypesEnum.TEAMMATE) {
    return {contentDataGroup, aroundActivity: undefined};
  }

  // If the the cache includes all unread, ok to return early.
  const {seenAt, autoSeenAt} = conversationState;
  if (!hasUnreadBeforeFetchedContent(conversationId, seenAt, autoSeenAt, contentDataGroup, userAlias)) {
    return {contentDataGroup, aroundActivity: undefined};
  }

  let hasRefetchedContent = true;

  const path = buildConversationContentUrl(conversationId, {
    aroundActivityDate: seenAt,
    shouldExpandMessages: true,
  });

  const updatedContentDataGroup = await fetchApiAsync(
    ApiConversationContentDataGroup,
    `${companyUrl}${path}`,
    {
      cancelToken,
      onResponseReceived: onRefetchResponseReceived,
    },
  ).catch((error) => {
    hasRefetchedContent = false;
    // If we have a network error, use the current cache.
    if (error instanceof HttpNetworkError) {
      return contentDataGroup;
    }

    throw error;
  });

  return {
    aroundActivity: hasRefetchedContent ? {date: seenAt ?? 0} : undefined,
    contentDataGroup: updatedContentDataGroup,
  };
}

function mergeConversationAndContentDataGroups(
  conversationId: number,
  getState: StateGetter,
  conversationDataGroup: DataGroupModel,
  contentDataGroup: DataGroupModel,
) {
  const updatedConversationDataGroup = maybeBackfillConversationInDataGroup(
    conversationId,
    getState,
    conversationDataGroup,
  );

  return mergeDataGroups(updatedConversationDataGroup, contentDataGroup);
}

function maybeBackfillConversationInDataGroup(
  conversationId: number,
  getState: StateGetter,
  dataGroup: DataGroupModel,
) {
  const {conversations = []} = dataGroup;

  // No need to do anything if the conversation already exists.
  const existingConversation = conversations.find((c) => c.id === conversationId);
  if (existingConversation) {
    return dataGroup;
  }

  // Use the conversation from the raw data without optimistic updates.
  const state = getState();
  const conversation = getOriginalConversations(state)[conversationId];

  // This should never happen.
  if (!conversation) {
    return dataGroup;
  }

  // Add the conversation to the data group.
  return {
    ...dataGroup,
    conversations: [...conversations, conversation],
  };
}

// Fetch the conversation from the server unless we already have it in the state.
// This is the solution retained for offline-mode V0: Loading a conversation from the cache has proven difficult in terms of conflict resolution,
// so in the meantime, try to load it from the state to allow offline browsing.
async function fetchApiConversationAsync(
  conversationId: number,
  getState: StateGetter,
  cancelToken: CancelToken | undefined,
  performanceId: string | undefined,
): Promise<DataGroupModel> {
  const {company} = getState().data;
  const getConversation = makeGetConversationById(conversationId);
  const path = buildConversationUrl(conversationId, {light: true});
  const existingConversation = getConversation(getState());

  if (performanceId) {
    addPerformanceInteractionMetadata(
      performanceId,
      'has_existing_conversation',
      Boolean(existingConversation),
    );
  }

  return existingConversation
    ? {conversations: [existingConversation]}
    : fetchApiAsync(ApiConversationDataGroup, `${company.url}${path}`, {cancelToken});
}

interface FetchApiConversationContentOptions {
  conversationId: number;
  getState: StateGetter;
  dispatch: Dispatch;
  cancelToken: CancelToken | undefined;
  onServerResponseReceived: HandlerOf<ServerResponseInfo>;
  onCachedResponseReceived: HandlerOf<CacheResponseInfo>;
}

async function fetchApiConversationContentAsync(
  options: FetchApiConversationContentOptions,
): Promise<DataGroupModel> {
  const {
    conversationId,
    getState,
    dispatch,
    cancelToken,
    onServerResponseReceived,
    onCachedResponseReceived,
  } = options;

  const getConversation = makeGetConversationById(conversationId);
  const getContentUpdatedAt = makeGetConversationContentUpdatedAt(getConversation);

  const path = buildConversationContentUrl(conversationId, {shouldExpandMessages: true});

  const onUpdatedNetworkResponse = (networkDataGroup: DataGroupModel) => {
    dispatch(updateDataAction(networkDataGroup, {isComingFromViewerFetch: true}));
  };

  const onNetworkError = (error: any) => {
    dispatch(maybeHandleConversationResponseErrorAction(conversationId, error));

    if (error instanceof HttpError) {
      return;
    }

    // Report non-HTTP errors to Bugsnag.
    reportToBugsnag(error);
  };

  const state = getState();
  const company = getCompany(state);
  const fetchOptions = computeConversationContentFetchOptions(state, conversationId);
  return fetchAndCacheApiAsync({
    apiType: ApiConversationContentDataGroup,
    resourceId: conversationId,
    path: `${company.url}${path}`,
    getLatestTimestamp: () => getContentUpdatedAt(getState(), null),
    httpOptions: {
      cancelToken,
      onResponseReceived: onServerResponseReceived,
      headers: {'X-Front-Prefetch': 'view'},
    },
    onCachedResponseReceived,
    cacheTimestamp: fetchOptions && fetchOptions.cacheTimestamp,
    onUpdatedNetworkResponse,
    onNetworkError,
  });
}

export function fetchMoreConversationAction(
  panelId: ViewerPanelId,
  page: ViewerPanelConversationPageState,
  direction: ScrollDirectionsEnum,
  referenceBoundary: ConversationTimelineBoundary,
): ThunkAction {
  return async (dispatch, getState) => {
    const state = getState();
    const context: FetchMoreConversationActionPayload = {panelId, pageUid: page.uid, direction};

    // If we have unseen messages auto expand the content when loading more
    const {originalConversationState} = page;
    const hasUnseenUnreadMessages =
      !isUndefined(originalConversationState) &&
      (!originalConversationState.seenAt ||
        (originalConversationState.seenAt && direction === ScrollDirectionsEnum.TOP
          ? originalConversationState.seenAt < referenceBoundary.activityDate
          : originalConversationState.seenAt > referenceBoundary.activityDate));
    const aroundActivityDate = findParametersActivityDate(page.parameters);
    const shouldExpandMessages =
      page.shouldExpandMessages ||
      hasUnseenUnreadMessages ||
      (isDefined(aroundActivityDate) && isFinite(aroundActivityDate));

    // Build the query string.
    const isOlder = direction === ScrollDirectionsEnum.TOP;
    const sortOrder = isOlder ? '-date' : 'date';
    const dateArgument = isOlder ? 'before' : 'after';

    const query = {
      sort: sortOrder,
      last_id: referenceBoundary.activityId,
      [dateArgument]: referenceBoundary.activityDate,
      expanded: shouldExpandMessages ? 'true' : undefined,
    };

    try {
      // Find the cancel token for this panel.
      const {cancelTokenId} = makeGetViewerPanel()(getState(), {panelId});
      const cancelToken = cancelTokenId ? findPanelCancelToken(cancelTokenId) : undefined;

      // Start the request right away.
      const company = getCompany(state);
      const path = addQueryToPath(`/conversations/${page.conversationId}/timeline`, query);
      const requestPromise = fetchApiAsync(ApiActivityPageDataGroup, `${company.url}${path}`, {
        cancelToken,
      });

      // Update the state to reflect this.
      const requestStartedAction: FetchMoreConversationStartedAction = {
        type: ViewerPanelPageActionsEnum.FETCH_MORE_CONVERSATION,
        payload: context,
      };
      dispatch(requestStartedAction);

      // Wait for the request to complete.
      const serverDataGroup = await requestPromise;

      // Update the state with the result.
      batch(() => {
        dispatch(updateDataAction(serverDataGroup, {shouldNotCascade: true}));

        const serverResult = fetchMoreConversationFromDataGroup(
          direction,
          referenceBoundary,
          serverDataGroup,
        );

        dispatch<FetchMoreConversationCompletedAction>({
          type: ViewerPanelPageActionsEnum.FETCH_MORE_CONVERSATION_COMPLETED,
          payload: {
            ...context,
            ...serverResult,
          },
        });
      });
    } catch (error) {
      // If this a cancellation, no need to update the state.
      if (error instanceof CancelError) {
        throw error;
      }

      // Update the state with the error.
      const requestFailedAction: FetchMoreConversationFailedAction = {
        type: ViewerPanelPageActionsEnum.FETCH_MORE_CONVERSATION_FAILED,
        payload: context,
      };
      dispatch(requestFailedAction);

      throw error;
    }
  };
}

export function editCommentAction(panelId: ViewerPanelId, commentUid: string | undefined): EditCommentAction {
  return {
    type: ViewerPanelActionsEnum.EDIT_COMMENT,
    payload: {panelId, commentUid},
  };
}

export function referenceActivityAction(conversationId: number, activityId: number): ThunkAction {
  return () => {
    const composer = findCommentComposer(conversationId);
    if (!composer) {
      reportToBugsnag(new Error('Comment composer not found'));
      return;
    }
    composer.setReferencedActivity(activityId);
    composer.focus();
  };
}

export function editLastCommentAction(panelId: ViewerPanelId, conversationId: number): ThunkAction {
  return (dispatch, getState) => {
    // Find the activities in the specified viewer panel.
    const state = getState();
    const userId = getUserId(state);
    const comments = getComments(state);
    const conversations = getConversations(state);
    const page = makeGetContentPage()(state, {panelId});
    const canEditComments = getCanEditComments(state);

    if (!page || page.type !== ViewerPanelPageTypesEnum.SINGLE_CONVERSATION || !canEditComments) {
      return null;
    }

    const pageActivities = makeGetPageActivities(() => page)(state, {});

    // Find the last comment from our user.
    const lastUserComment = _(pageActivities)
      .map((a) => a.visualType === ActivityVisualTypesEnum.COMMENT && a)
      .compact()
      .map((a) => comments[a.commentUid])
      .compact()
      .sortBy((c) => c.sentAt)
      .findLast((c) => c.authorId === userId);

    // Find the last sending comment from our user
    const conversation = conversations[conversationId];
    const userDraftComments = maybeGetUserDraftComments(state, conversation);
    const lastSendingUserComment = _(userDraftComments)
      .filter((d) => d.status === CommentStatusesEnum.PENDING || d.status === CommentStatusesEnum.SENDING)
      .compact()
      .sortBy((c) => c.sentAt)
      .last();

    // Use the last sending comment in priority over the last posted comment.
    const commentToEdit = lastSendingUserComment || lastUserComment;

    // If none was found, nothing to do.
    if (!commentToEdit) {
      return null;
    }

    // Otherwise, edit this comment.
    return dispatch(editCommentAction(panelId, commentToEdit.uid));
  };
}

function maybeGetUserDraftComments(state: AppState, conversation?: ConversationModel) {
  if (!conversation) {
    return [];
  }

  return makeGetDraftComments(() => conversation)(state, {});
}

export function popoutInternalComposerAction(commentDraftUid: string): ThunkAction {
  return (dispatch, getState) => {
    // Create the internal composer page.
    const buildDefaultParameters = getBuildDefaultInboxesParameters(getState());
    const parameters = buildDefaultParameters({
      folder: TeammatesFoldersEnum.DISCUSSIONS,
    });

    const page: ViewerPanelEmptyPageState = {
      uid: randomString(),
      type: ViewerPanelPageTypesEnum.NO_CONVERSATION,
      parameters,
      cellsContext: makeCellsContext(getState(), parameters),
    };

    // Create the viewer panel for the internal composer page.
    const popupPanelId = makeViewerPanelId();
    const popupAppPanelId = getFirstAppPanelId(getState());
    const viewerPanelState: ViewerPanelState = {
      id: popupPanelId,
      type: PanelTypesEnum.VIEWER,
      sequence: findPanelSequence(),
      popupAppPanelId,
      shouldForcePopout: false,
      pages: {current: page, next: undefined},
      composeCommentUid: commentDraftUid,
      cancelTokenId: undefined,
    };

    dispatch(setPanelAction(viewerPanelState));
  };
}

export function forceInternalComposerPopoutAction(panelId: ViewerPanelId): ForceInternalComposerPopoutAction {
  return {
    type: ViewerPanelActionsEnum.FORCE_INTERNAL_COMPOSER_POPOUT,
    payload: {panelId},
  };
}

export function popoutViewerAction(
  parameters: InboxesParameters,
  location?: PopoutLocationsEnum,
): ThunkAction {
  return (dispatch, getState) => {
    const appPanelId = getFirstAppPanelId(getState());

    trackInterfaceEvent(InterfaceEventsEnum.POPOUT_VIEWER, {location});
    dispatch(navigateViewerAsyncAction(makeViewerPanelId(), parameters, {popupAppPanelId: appPanelId}));
  };
}

export function popoutConversationViewerAction(
  conversationId: number,
  activityId?: number,
  inheritParameters?: InboxesParameters,
  location?: PopoutLocationsEnum,
): ThunkAction {
  return (dispatch, getState) => {
    const selection = normalInboxesSelection([conversationId]);
    const activity: InboxesParametersActivity | undefined = isDefined(activityId)
      ? {id: activityId}
      : undefined;

    const parameters: InboxesConversationsParameters =
      inheritParameters && inheritParameters?.type !== InboxesParametersTypesEnum.TEAMMATE_MESSAGES
        ? {...inheritParameters, selection, activity}
        : getBuildDefaultInboxesParameters(getState())({selection, activity});

    dispatch(popoutViewerAction(parameters, location));
  };
}

function hasUnreadBeforeFetchedContent(
  conversationId: number,
  seenAt: number | undefined,
  autoSeenAt: number | undefined,
  dataGroup: DataGroupModel,
  userAlias: string,
) {
  const {activities = [], timeline} = dataGroup;

  // There is nothing more to fetch.
  if (!timeline?.topBoundary) {
    return false;
  }

  // If we have a never read at mention in the current list no need to load previous
  const userMention = activities.find((a) => isActivityInConversationUnreadMention(a, userAlias, autoSeenAt));
  if (userMention) {
    return false;
  }

  // If we have never seen a rule notification on the conversation no need to load previous
  const ruleNotification = activities.find((a) => isActivityInConversationRuleNotification(a, autoSeenAt));
  if (ruleNotification) {
    return false;
  }

  // If we've never read the conversation, load the earliest activities
  const hasUnreadBefore = !seenAt || activities.every((activity) => activity.date > seenAt);

  // Temporary instrumentation to help troubleshoot https://frontjira.atlassian.net/browse/PB-19727
  if (hasUnreadBefore) {
    logger.warn('Refetching conversation from the top', {
      conversation_id: conversationId,
      original_seen_at: seenAt,
    });
  }

  return hasUnreadBefore;
}

export function editDraftMessageAction(panelId: ViewerPanelId, messageUid: string): EditDraftMessageAction {
  return {
    type: ViewerPanelActionsEnum.EDIT_DRAFT_MESSAGE,
    payload: {panelId, messageUid},
  };
}

export function expandAllMessagesAsyncAction(panelId: ViewerPanelId): ThunkAsyncAction {
  return async (dispatch, getState) => {
    // The expand all UI is in the top bar.
    const page = makeGetTopbarPage()(getState(), {panelId});
    if (page.type !== ViewerPanelPageTypesEnum.SINGLE_CONVERSATION) {
      return;
    }

    // TODO: Fetch expanded messages only if needed. On desktop, all message content is loaded by default.
    // TODO: Update the existing viewer panel page instead of navigating to a new page.
    await dispatch(
      navigateViewerAsyncAction(panelId, page.parameters, {
        shouldExpandMessages: true,
      }),
    );
  };
}

export function collapseAllMessagesAction(panelId: ViewerPanelId): ThunkAction {
  return (dispatch, getState) => {
    // The expand all UI is in the top bar.
    const page = makeGetTopbarPage()(getState(), {panelId});
    if (page.type !== ViewerPanelPageTypesEnum.SINGLE_CONVERSATION) {
      return;
    }

    dispatch<AllMessagesCollapsedAction>({
      type: ViewerPanelPageActionsEnum.ALL_MESSAGES_COLLAPSED,
      payload: {panelId, pageUid: page.uid},
    });
  };
}

export function jumpToActivityAction(
  panelId: ViewerPanelId,
  targetActivity: InboxesParametersActivity,
  sourceActivityId?: number,
): ThunkAction {
  return (dispatch, getState) => {
    // Find the current page for this panel.
    const page = makeGetContentPage()(getState(), {panelId});
    if (page.type !== ViewerPanelPageTypesEnum.SINGLE_CONVERSATION) {
      return;
    }

    // If the activity is already on the page, jump to it directly.
    if (isConversationAndActivityOnPage(page, targetActivity)) {
      dispatch<JumpedAction>({
        type: ViewerPanelPageActionsEnum.JUMPED,
        payload: {
          panelId,
          pageUid: page.uid,
          jumpUid: randomString(),
          targetActivity,
          sourceActivityId,
        },
      });
      return;
    }

    // Otherwise, navigate.
    const parameters: InboxesParameters = {...page.parameters, activity: targetActivity};
    dispatch(
      navigateViewerAsyncAction(panelId, parameters, {
        shouldExpandMessages: page.shouldExpandMessages,
        jumpSourceActivityId: sourceActivityId,
      }),
    );
  };
}

/*
 * Type guard.
 */

export function isViewerPanelPageAction(action: AppAction): action is ViewerPanelPageAction {
  return RViewerPanelPageAction.is(action);
}

/*
 * Combined action type.
 */

export type ViewerPanelPageAction =
  | ViewerPageRefetchStartedAction
  | FetchConversationCompletedAction
  | FetchConversationFailedAction
  | FetchConversationGraceTimeout
  | FetchMoreConversationStartedAction
  | FetchMoreConversationCompletedAction
  | FetchMoreConversationFailedAction
  | AllMessagesCollapsedAction
  | JumpedAction;

export type ViewerPanelAction =
  | ViewerNavigatedAction
  | EditCommentAction
  | EditDraftMessageAction
  | ActivityViewerActions
  | ViewerPanelPageAction
  | ForceInternalComposerPopoutAction;
